import * as React from 'react'
import { useCookie } from 'web-ui/dist/helpers/hooks/use-cookie'
import {
  BRAND_UUID_COOKIE_EXPIRY_DAYS,
  BRAND_UUID_COOKIE_KEY,
} from 'web-ui/dist/config/data/cookie-storage'

import {
  User,
  getCurrentUser as useCurrentUrer,
  login as useLogin,
  logout as useLogOut,
  isAuthenticated,
} from '../../helpers/auth'

export interface UserContextProps {
  authenticated: boolean
  isPremium: boolean
  brandUuid?: string

  login(token: string): void

  logout(): void

  switchBrand(uuid: string, isPremium: boolean): string

  getCurrentUser(key?: string): User | undefined
}

export const UserContext = React.createContext<UserContextProps>({
  authenticated: false,
  isPremium: false,
  login: () => undefined,
  logout: () => undefined,
  getCurrentUser: () => undefined,
  switchBrand: () => '',
})

interface State {
  authenticated: boolean
  isPremium: boolean
  brandUuid?: string
}

export const UserProvider: React.FC = ({ children }) => {
  const brandCookie = useCookie(BRAND_UUID_COOKIE_KEY, { expires: BRAND_UUID_COOKIE_EXPIRY_DAYS })
  const [brandInfo, setBrandInfo] = React.useState<State>({
    authenticated: isAuthenticated(),
    brandUuid: brandCookie.getValue(),
    isPremium: false,
  })

  const switchBrand = React.useCallback(
    (brandUuid, isPremium) => {
      if (brandUuid !== brandCookie.getValue() || brandInfo.isPremium !== isPremium) {
        brandCookie.setValue(brandUuid)
        setBrandInfo(currentInfo => ({ ...currentInfo, isPremium, brandUuid }))
      }

      location.href = `${window.env.BRAND_BASE_URL}/brand-dashboard/${brandUuid}`

      return brandUuid
    },
    [brandCookie.setValue, brandInfo, setBrandInfo]
  )

  const login = (token: string) => {
    useLogin(token)
    setBrandInfo(currentInfo => ({ ...currentInfo, authenticated: true }))
  }

  const logout = async () => {
    setBrandInfo({ brandUuid: undefined, authenticated: false, isPremium: false })
    brandCookie.deleteKey()

    try {
      await fetch(`${window.env.GEN1_BASE_URL}/sign_out`, {
        credentials: 'include',
        method: 'DELETE',
      })
      window.location.assign(window.env.GEN1_BASE_URL)
      useLogOut()
    } catch {
      console.error('Something went wrong')
    }
  }

  const getCurrentUser = (key?: string): User | undefined => {
    const user = useCurrentUrer()

    if (!key) {
      return user
    }

    return user ? user[key] : undefined
  }

  return (
    <UserContext.Provider
      value={{
        ...brandInfo,
        switchBrand,
        login,
        logout,
        getCurrentUser,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const UserConsumer = UserContext.Consumer



// WEBPACK FOOTER //
// ./src/components/UserProvider/UserProvider.tsx